import React from "react";

interface CountryDropdownProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  required?: boolean;
  id?: string;
  name?: string;
  label?: string;
}

const hardcodedCountries = ["United Kingdom", "United States", "Canada"];
const additionalCountries = [
  "Afghanistan",
  "Albania",
  "Algeria",
  "Andorra",
  "Angola",
  "Antigua and Barbuda",
  "Argentina",
  "Armenia",
  "Australia",
  "Austria",
  "Azerbaijan",
  "Bahamas",
  "Bahrain",
  "Bangladesh",
  "Barbados",
  "Belarus",
  "Belgium",
  "Belize",
  "Benin",
  "Bhutan",
  "Bolivia",
  "Bosnia and Herzegovina",
  "Botswana",
  "Brazil",
  "British Indian Ocean Territory",
  "British Virgin Islands",
  "Brunei",
  "Bulgaria",
  "Burkina Faso",
  "Burundi",
  "Cabo Verde",
  "Cambodia",
  "Cameroon",
  "Cayman Islands",
  "Central African Republic",
  "Chad",
  "Chile",
  "China",
  "Colombia",
  "Comoros",
  "Congo (Congo-Brazzaville)",
  "Costa Rica",
  "Croatia",
  "Cuba",
  "Cyprus",
  "Czechia (Czech Republic)",
  "Democratic Republic of the Congo",
  "Denmark",
  "Djibouti",
  "Dominica",
  "Dominican Republic",
  "Ecuador",
  "Egypt",
  "El Salvador",
  "Equatorial Guinea",
  "Eritrea",
  "Estonia",
  'Eswatini (fmr. "Swaziland")',
  "Ethiopia",
  "Fiji",
  "Finland",
  "France",
  "Gabon",
  "Gambia",
  "Georgia",
  "Germany",
  "Ghana",
  "Greece",
  "Grenada",
  "Guatemala",
  "Guinea",
  "Guinea-Bissau",
  "Guyana",
  "Haiti",
  "Honduras",
  "Hong Kong",
  "Hungary",
  "Iceland",
  "India",
  "Indonesia",
  "Iran",
  "Iraq",
  "Ireland",
  "Israel",
  "Italy",
  "Jamaica",
  "Japan",
  "Jersey",
  "Jordan",
  "Kazakhstan",
  "Kenya",
  "Kiribati",
  "Kuwait",
  "Kyrgyzstan",
  "Laos",
  "Latvia",
  "Lebanon",
  "Lesotho",
  "Liberia",
  "Libya",
  "Liechtenstein",
  "Lithuania",
  "Luxembourg",
  "Madagascar",
  "Malawi",
  "Malaysia",
  "Maldives",
  "Mali",
  "Malta",
  "Marshall Islands",
  "Mauritania",
  "Mauritius",
  "Mexico",
  "Micronesia",
  "Moldova",
  "Monaco",
  "Mongolia",
  "Montenegro",
  "Morocco",
  "Mozambique",
  "Myanmar (formerly Burma)",
  "Namibia",
  "Nauru",
  "Nepal",
  "Netherlands",
  "New Zealand",
  "Nicaragua",
  "Niger",
  "Nigeria",
  "North Korea",
  "North Macedonia",
  "Norway",
  "Oman",
  "Pakistan",
  "Palau",
  "Palestine State",
  "Panama",
  "Papua New Guinea",
  "Paraguay",
  "Peru",
  "Philippines",
  "Poland",
  "Portugal",
  "Qatar",
  "Romania",
  "Russia",
  "Rwanda",
  "Saint Kitts and Nevis",
  "Saint Lucia",
  "Saint Vincent and the Grenadines",
  "Samoa",
  "San Marino",
  "Sao Tome and Principe",
  "Saudi Arabia",
  "Senegal",
  "Serbia",
  "Seychelles",
  "Sierra Leone",
  "Singapore",
  "Slovakia",
  "Slovenia",
  "Solomon Islands",
  "Somalia",
  "South Africa",
  "South Korea",
  "South Sudan",
  "Spain",
  "Sri Lanka",
  "Sudan",
  "Suriname",
  "Sweden",
  "Switzerland",
  "Syria",
  "Tajikistan",
  "Tanzania",
  "Thailand",
  "Timor-Leste",
  "Togo",
  "Tonga",
  "Trinidad and Tobago",
  "Tunisia",
  "Turkey",
  "Turkmenistan",
  "Tuvalu",
  "Uganda",
  "Ukraine",
  "United Arab Emirates",
  "Uruguay",
  "Uzbekistan",
  "Vanuatu",
  "Vatican City",
  "Venezuela",
  "Vietnam",
  "Yemen",
  "Zambia",
  "Zimbabwe",
];
const allCountries = [...hardcodedCountries, ...additionalCountries];

const CountryDropdown: React.FC<CountryDropdownProps> = ({
  value,
  onChange,
  required = false,
  id = "country-of-citizenship",
  name = "Country of citizenship",
  label = "Country of citizenship:",
}) => {
  // Normalize country name for better matching
  const normalizeCountryName = (countryName: string): string => {
    if (!countryName || typeof countryName !== "string") return "";

    // Convert to proper case and handle common variations
    const normalized = countryName
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase()) // Convert to proper case
      .replace(/\bUsa\b/g, "United States")
      .replace(/\bUk\b/g, "United Kingdom")
      .replace(/\bNz\b/g, "New Zealand");

    // console.log(`🔍 Country normalization: "${countryName}" -> "${normalized}"`);
    return normalized;
  };

  // Normalize the input value
  const normalizedValue = normalizeCountryName(value);

  // Check if the normalized value exists in the country list (case-insensitive)
  const isValueInList =
    normalizedValue && typeof normalizedValue === "string"
      ? allCountries.some((country) => country.toLowerCase() === normalizedValue.toLowerCase())
      : false;

  // Find the exact match from the list to use as the select value
  const selectValue = isValueInList
    ? allCountries.find((country) => country.toLowerCase() === normalizedValue.toLowerCase()) || ""
    : "";

  const hasValue = normalizedValue && normalizedValue.trim() !== "";

  return (
    <div style={{ marginBottom: 16, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
      <label htmlFor={id} style={{ fontWeight: 500, display: "block", marginBottom: 4 }}>
        {label}
      </label>
      <select
        id={id}
        name={name}
        value={isValueInList ? selectValue : hasValue ? "OTHER_CUSTOM" : ""}
        onChange={onChange}
        style={{
          width: "100%",
          maxWidth: "100%",
          padding: "8px",
          borderRadius: 4,
          border: "1px solid #ccc",
          boxSizing: "border-box",
        }}
        required={required}
      >
        <option value="" disabled>
          ---Select---
        </option>
        {hardcodedCountries.map((country) => (
          <option key={country} value={country}>
            {country}
          </option>
        ))}
        {/* Divider */}
        <option disabled>---</option>

        {/* Dynamically added countries */}
        {additionalCountries.map((country) => (
          <option key={country} value={country}>
            {country}
          </option>
        ))}

        {/* Show unmatched value as "Other" option */}
        {hasValue && !isValueInList && (
          <>
            <option disabled>---</option>
            <option value="OTHER_CUSTOM" style={{ backgroundColor: "#fff3cd", color: "#856404" }}>
              {value}
            </option>
          </>
        )}
      </select>

      {hasValue && !isValueInList && (
        <div
          style={{
            marginTop: 4,
            padding: "6px 8px",
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: 4,
            fontSize: "12px",
            color: "#856404",
          }}
        >
          ⚠️ "{value}" - Not found in dropdown list
        </div>
      )}
    </div>
  );
};

export default CountryDropdown;
